import React, { Fragment } from "react";

import { ImageData } from "../../data";
import { offeringConfig } from "../offering_config";
import { OfferingDetailsDayPassWithCapacity } from "../template_offering_details_daypass_with_capacity";
import { Spacer } from "../spacer";
import { Text } from "../text";
import { TextLink } from "../text_link";
import { useUrbanplaceAnalytics } from "./urbanplace_analytics";
import {
  generalInfo,
  partnerConfig,
  testimonials,
} from "./urbanplace_partner_config";
import { lazyWithRetry } from "../../core/lazy_with_retry";

const Theme = lazyWithRetry(() => import("./urbanplace_theme"));

const faqs = [
  {
    question: "Can I cancel my day office pass purchase?",
    answer:
      "Yes, the Day Office Pass can be cancelled and fully refunded 48 hours before the start of the booking.",
  },

  {
    question: "Can I change the date of my booking?",
    answer:
      "Yes. Please either call us or reply to the confirmation email and we will take care of it.",
  },

  {
    question: "Can I use the meeting rooms?",
    answer: (
      <Text>
        <TextLink
          href={`/offerings/${offeringConfig.Urbanplace.slugConfig.meetingRooms}`}
        >
          Meeting rooms will need to be booked
        </TextLink>{" "}
        separately.
      </Text>
    ),
  },

  {
    question: "Can I use the printer?",
    answer:
      "Yes, please get in touch with the front desk and we will assist you.",
  },

  {
    question: "Can I meet with clients or bring a guest into the common area?",
    answer: "You will have to purchase a Day Pass for each of your guests.",
  },

  {
    question: "Where can I keep and heat my lunch?",
    answer:
      "The kitchen, refrigerator and microwave is available for your use.",
  },

  {
    question: "Do you offer coffee or tea?",
    answer: "Yes, coffee and tea are complimentary.",
  },
];

const upDayOfficeRothschildDesktopImages: ImageData[] = [
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/urbanplace/U03B4010.jpg",
    width: 5328,
    height: 2997,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/urbanplace/Locations/Rothschild/01_IMG_5766.JPG",
    width: 5184,
    height: 3456,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/urbanplace/Locations/Rothschild/02_office+rothschild.jpeg",
    width: 1600,
    height: 1200,
  },
];

const upDayOfficeRothschildMobileImages: ImageData[] =
  upDayOfficeRothschildDesktopImages;

const upDayOfficeShalomDesktopImages: ImageData[] = [
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/urbanplace/U03B4010.jpg",
    width: 5328,
    height: 2997,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/urbanplace/Locations/Shalom+Towers/01_IMG_2242-Edit.jpg",
    width: 1200,
    height: 800,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/urbanplace/Locations/Shalom+Towers/02_IMG_2227-Edit.jpg",
    width: 1200,
    height: 800,
  },
];

const upDayOfficeShalomMobileImages: ImageData[] =
  upDayOfficeShalomDesktopImages;

const upDayOfficeJerusalemDesktopImages: ImageData[] = [
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/urbanplace/U03B4010.jpg",
    width: 5328,
    height: 2997,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/urbanplace/Locations/JLM/01_U03B4779.jpg",
    width: 5760,
    height: 3240,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/urbanplace/Locations/JLM/02_U03B4777.jpg",
    width: 5760,
    height: 3240,
  },
];

const upDayOfficeJerusalemMobileImages: ImageData[] =
  upDayOfficeJerusalemDesktopImages;

const upRothschildLocationIDs = [
  "f513a941-51c8-4e12-afea-43aaf75f6a1a", // production
  "713400c3-65af-46c9-93ef-7dbbb45a6850", // staging
];

const upShalomLocationIDs = [
  "47f9027e-00db-4bd6-ad3c-33ca32209266", // production
  "d465e083-9d69-4caf-9e39-19d5b35979d2", // staging
];

export function OfferingUrbanplaceDayOfficePass() {
  useUrbanplaceAnalytics();

  return (
    <Fragment>
      <Theme />
      <OfferingDetailsDayPassWithCapacity
        showAddress={false}
        useLegacyImages={true}
        showDirections
        getDesktopImagesBasedOnLocation={(location) => {
          if (!location) {
            return upDayOfficeJerusalemDesktopImages;
          }

          if (upRothschildLocationIDs.includes(location.id)) {
            return upDayOfficeRothschildDesktopImages;
          }

          if (upShalomLocationIDs.includes(location.id)) {
            return upDayOfficeShalomDesktopImages;
          }

          return upDayOfficeJerusalemDesktopImages;
        }}
        getMobileImagesBasedOnLocation={(location) => {
          if (!location) {
            return upDayOfficeJerusalemMobileImages;
          }

          if (upRothschildLocationIDs.includes(location.id)) {
            return upDayOfficeRothschildMobileImages;
          }

          if (upShalomLocationIDs.includes(location.id)) {
            return upDayOfficeShalomMobileImages;
          }

          return upDayOfficeJerusalemMobileImages;
        }}
        partnerConfig={{
          ...partnerConfig,
        }}
        data={{
          description: (
            <Fragment>
              <Text>
                Not everyone needs an office, but everyone needs a place to
                work. Our beautiful renovated open space is the perfect place
                for those days where you just need to get out of the house and
                work on a project.
              </Text>
              <Spacer size={8} />
              <Text>
                Get a private office for the day and enjoy member benefits for
                that day.
              </Text>
            </Fragment>
          ),
          testimonials: testimonials,
          faqs: faqs,
          generalInfo,
        }}
      />
    </Fragment>
  );
}
